/**
 * Sentry for the browser — inert unless REACT_APP_SENTRY_DSN is set at BUILD time
 * (CRA inlines REACT_APP_* into the bundle, so preview/dev builds ship with it empty).
 *
 * A frontend DSN is public by design: it can only submit events, never read them.
 */
import * as Sentry from "@sentry/react";

const dsn = process.env.REACT_APP_SENTRY_DSN;

// One key, both environments: the preview pod and the deployed site build from the same .env,
// so the environment is decided at RUNTIME by hostname. Filter on it in Sentry.
const environment =
  process.env.REACT_APP_SENTRY_ENVIRONMENT ||
  (typeof window === "undefined" ? "production"
    : /localhost|127\.0\.0\.1/.test(window.location.hostname) ? "local"
    : window.location.hostname.includes("preview") ? "preview"
    : "production");

// Preview is where code is edited live: a hot-reload can render a half-applied file for a few
// seconds and page the owner with "X is not defined" (4 of those on 2026-09-10). The backend
// already stays silent in preview (iter376) — the browser now does too. Local stays silent as well.
const shouldReport = Boolean(dsn) && environment === "production";

if (shouldReport) {
  Sentry.init({
    dsn,
    environment,
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    sampleRate: 1.0,
    // Errors only. No performance tracing, no session replay — residents' screens are
    // full of names, addresses and phone numbers.
    tracesSampleRate: 0,
    sendDefaultPii: false,
    // Browser-extension and third-party noise (Cal.com's embed, ad blockers) is not our bug.
    // `findLast is not a function` comes from PostHog's web-vitals bundle (loaded by the
    // analytics snippet in index.html) on browsers older than Chrome 97 / Safari 15.4. Our own
    // code never calls findLast, so the app is fine — it was just burning Sentry quota.
    ignoreErrors: [
      "ResizeObserver loop",
      "Non-Error promise rejection captured",
      "findLast is not a function",
      "findLastIndex is not a function",
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /app\.cal\.com/i, /posthog/i, /web-vitals/i],
  });
}

export const sentryEnabled = shouldReport;
export { Sentry };
